import type { BlinkEvent, PointerInputEvent, SuppressionLevel } from "../core/types.ts";

/**
 * The seam every game mechanic sits behind.
 *
 * Spec §4's rule — the detector does not know a game exists — is what makes
 * several mechanics testable in an afternoon. This interface is the other half
 * of it: mechanics do not know each other exists either, so swapping one is a
 * reference assignment rather than a rewiring.
 *
 * ## The DOM rule
 *
 * A mechanic may write **only inline style properties of elements handed to it
 * at construction**. No `document.*`, no queries, no `getBoundingClientRect`,
 * no canvas. Size-dependent motion is expressed as a percentage so the mechanic
 * never needs to read layout.
 *
 * That is not architectural taste. Vitest runs in the node environment here and
 * jsdom is not installed, so a mechanic that stays inside this rule is testable
 * against a five-line fake and one that leaves it is not testable at all.
 *
 * ## The clock rule
 *
 * Events carry FRAME time. `update()` is called with `performance.now()`. They
 * differ by tens of seconds on a real page. Anything time-dependent must be
 * resolved inside `update`, from the `t` it is given — see the long comment in
 * `fatigueBlur.ts` on the bug that rule was written after.
 *
 * ## Why the consumer methods are optional
 *
 * Each mechanic declares which channel it lives on by which methods it defines.
 * `FatigueBlur` consumes pressure and not blinks; `Reload` consumes blinks and
 * not pressure. Forcing empty bodies for the rest would erase that signal, and
 * that signal is the most useful thing about reading one of these files.
 */

export type MechanicId =
  | "none"
  | "fatigue"
  | "reload"
  | "blackout"
  | "balance"
  | "lantern"
  | "tempo"
  | "kart";

/** Picker order. `none` first, so the harness default is the measuring one. */
export const MECHANIC_IDS: readonly MechanicId[] = [
  "none",
  "fatigue",
  "reload",
  "blackout",
  "balance",
  "lantern",
  "tempo",
  "kart",
];

/**
 * Frame delta, clamped.
 *
 * Three mechanics integrate over time, and every one of them would otherwise
 * have the same bug: a backgrounded tab, a stalled camera or a slow first paint
 * leaves a hole in the timeline, and charging the whole hole to the simulation
 * advances the world by a minute in one step. Same reasoning as
 * `MAX_CLOSED_STEP_MS` in `core/metrics.ts`, and the same number.
 *
 * Returns 0 on the first call, so nothing moves before there is a delta to move
 * it by.
 */
export class FrameClock {
  static readonly MAX_STEP_MS = 100;

  #last: number | null = null;

  step(t: number): number {
    if (this.#last === null) {
      this.#last = t;
      return 0;
    }
    const dt = Math.min(t - this.#last, FrameClock.MAX_STEP_MS);
    this.#last = t;
    return dt > 0 ? dt : 0;
  }

  reset(): void {
    this.#last = null;
  }
}

/**
 * Amplitude a genuine full blink actually produces on this build.
 *
 * Measured over 155 emitted blinks across six real recordings, 396 seconds:
 *
 *     duration   p10 167   p50 267   p90 465   p99 597 ms
 *     amplitude  p10 0.51  p50 0.62  p90 0.74  p99 0.81
 *
 * Mechanics used to divide by the detector's `completeAmplitude` (0.70), and
 * that was a category error: `completeAmplitude` is a *classification*
 * threshold — the line above which a blink is called complete — while what a
 * mechanic needs is a *normalisation* constant, the amplitude at which you have
 * earned everything there is to earn. Borrowing one for the other made a median
 * blink worth 88% of a full one, when a median blink is by definition not a
 * full blink.
 *
 * ## This number is wrong on purpose, and it is documented so it gets fixed
 *
 * It should be 1.0. It is 0.75 because `CLOSED_FRACTION.blendshape` in
 * `core/closure.ts` assumes a shut eye reads `eyeBlink ≈ 1.0`, and measurement
 * says it reads 0.78–0.87 — so every amplitude leaves the detector compressed
 * by 1.20×–1.43×. When spec §3 calibration lands and feeds the real floor to
 * `ClosureSignal.setClosedReference`, amplitudes decompress and **this constant
 * must go to 1.0**. The mechanics are insulated from that bug rather than
 * fixed of it.
 */
export const FULL_CREDIT_AMPLITUDE = 0.75;

/**
 * How much of an effect one blink has earned, 0..1.
 *
 * Continuous, and never a function of `BlinkEvent.kind` — design doc §5 asks
 * for partial blink, partial effect, and `kind` is a coin flip at the
 * amplitudes this detector produces. One shared implementation so the three
 * mechanics that need it cannot drift apart.
 */
export function relief(amplitude: number): number {
  const v = amplitude / FULL_CREDIT_AMPLITUDE;
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

export interface Readout {
  value: string;
  label: string;
  /** Renders in the HUD's alert colour. Reuses `.hud-cell.armed`. */
  alert?: boolean;
}

export interface Mechanic {
  readonly id: MechanicId;
  /**
   * The verb, for the tap-surface hint. Static on purpose.
   *
   * It sits over the player's own face as a persistent affordance label. A
   * string that changed with state would turn it into a second readout
   * competing with the HUD, and design doc §7 asks for the opposite: playing
   * correctly should feel like not thinking about your eyes. State goes in
   * `readout()`; only the verb goes here.
   */
  readonly hint: string;

  onBlink?(event: BlinkEvent): void;
  onSuppression?(level: SuppressionLevel): void;
  onPointer?(event: PointerInputEvent): void;

  /** @param t render-loop time from performance.now(). */
  update(t: number): void;

  /** `null` hides the HUD cell — for a mechanic with nothing to say yet. */
  readout(): Readout | null;

  /**
   * Must clear every inline style it ever wrote.
   *
   * The failure this prevents is a blur that never lifts because the mechanic
   * that set it is no longer the one being updated. `src/game/mechanic.test.ts`
   * enforces it across the whole registry, including mechanics not yet written.
   */
  reset(): void;
}

/**
 * No game. The measuring state, and the harness default.
 *
 * Exists so `mechanic` is always a `Mechanic` and never `Mechanic | null` —
 * otherwise every call site becomes a double-optional chain.
 */
export class NullMechanic implements Mechanic {
  readonly id = "none" as const;
  readonly hint = "tap anywhere on every blink";

  update(): void {}

  readout(): Readout | null {
    return null;
  }

  reset(): void {}
}
